import React from 'react';
import {LogSource} from 'commerceAPI/custom-apis/CustomShopperSystem/ICustomShopperSystem';

import usePathname from 'coreHooks/usePathname/usePathname';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import type {
	IComponentErrorBoundaryProps,
	IComponentErrorBoundaryState,
	IErrorFunctionalComponentProps,
} from './IComponentErrorBoundary';

const ErrorFunctionalComponent = ({
	fallback,
	error,
	stack,
}: IErrorFunctionalComponentProps) => {
	const pathname = usePathname();
	const {managerRepository} = useGeneralProvider();
	const logProvider = managerRepository.getLogMgr().getLogProvider();

	if (logProvider && error && stack) {
		logProvider.error(`${error} at "${pathname}"`, LogSource.COMPONENT, stack);
	}

	if (fallback) {
		return fallback;
	} else {
		return <></>;
	}
};

/**
 * Represents ComponentErrorBoundary
 */
class ComponentErrorBoundary extends (React as any).Component<
	IComponentErrorBoundaryProps,
	IComponentErrorBoundaryState
> {
	/**
	 * @constructor
	 * @param {object} props - props
	 */
	constructor(props: IComponentErrorBoundaryProps) {
		super(props);

		this.state = {
			hasError: false,
			error: null,
			stack: null,
		};
	}

	/**
	 * Called when the component is mounted or updated
	 * @param props - component props
	 * @param state - component state
	 * @returns {Partial<IComponentErrorBoundaryState> | null} - partial state or null
	 */
	static getDerivedStateFromProps(
		props: IComponentErrorBoundaryProps,
		state: IComponentErrorBoundaryState,
	): Partial<IComponentErrorBoundaryState> | null {
		if (state.hasError && props.resetKey !== state.resetKey) {
			return {hasError: false, error: null, stack: null, resetKey: props.resetKey};
		}

		if (!state.hasError && state.resetKey !== props.resetKey) {
			return {resetKey: props.resetKey};
		}

		return null;
	}

	/**
	 * Called when an error is catched
	 * @param {object} error - error
	 * @param {object} errorInfo - info
	 */
	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error(error);

		this.setState({
			hasError: true,
			error: error,
			stack: errorInfo,
		});
	}

	/**
	 * Renders the component
	 */
	render(): React.ReactNode {
		if (this.state.hasError) {
			return (
				<ErrorFunctionalComponent
					fallback={this.props.fallback}
					error={this.state.error}
					stack={this.state.stack}
				/>
			);
		}

		return this.props.children;
	}
}

export default ComponentErrorBoundary as React.ComponentType<IComponentErrorBoundaryProps>;
